import {useRouter} from "next/router"
import React, {useContext, useEffect, useRef, useState} from "react"
import {useTranslation} from "next-i18next"
import {filterUrl} from "../FilterUrl"
import {priceRangeDelimiter} from "../Config"
import Slider from "../../slider/Slider"
import Typography from "../../reusable/Typography/Typography"
import {CollectionName} from "../../../constants"
import {gaCategories, gaEvents} from "../../../config/googleAnalytics/events"
import FilterContext from "../../../context/FilterContext"
import AnalyticsContext from "../../../context/AnalyticsContext"
import Checkbox from "../../reusable/components/Checkbox/Checkbox"
import CheckoutContext from "../../../context/CheckoutContext"
import urlParams from "../../../utility/UrlParams"
import DataContext from "../../../context/DataContext"

export type Values = {
  min: number
  max: number
}

type Props = {
  valuesRange: Values
  selectedValues: number[]
  planLength: number
  collectionName: CollectionName
  setSelectedPriceRange: (value) => void
  customUrlBaseValue?: string
}

const PriceFilterSlider = ({
	valuesRange,
	selectedValues,
	planLength,
	collectionName,
	setSelectedPriceRange,
	customUrlBaseValue
}: Props): React.ReactElement => {
	const {t} = useTranslation("filters")
	const router = useRouter()
	const firstInputRef = useRef(null)
	const secondInputRef = useRef(null)
	const {categoryOrBrandName} = React.useContext(FilterContext)
	const {reactGA} = useContext(AnalyticsContext)
	const [isTotalPrice, setIsTotalPrice] = useState(false)
	const [firstInput, setFirstInput] = useState<any>(selectedValues[0])
	const [secondInput, setSecondInput] = useState<any>(selectedValues[1])
	const {data, setData} = useContext(CheckoutContext)
	const checkoutData = data
	const isBrandWorld = router.pathname.includes("/brand-world")
	const {brandWorld} = useContext(DataContext)
	const onAfterChange = value => {
		const url = isBrandWorld ? `${filterUrl({...router.query, [urlParams.priceRange]: selectedValues.join(priceRangeDelimiter)}, "priceRange", value.join(priceRangeDelimiter), CollectionName.BrandWorld, CollectionName.BrandWorld, brandWorld.brandSlug)}#products-listing` : filterUrl({...router.query, [urlParams.priceRange]: selectedValues.join(priceRangeDelimiter)}, "priceRange", value.join(priceRangeDelimiter), collectionName, categoryOrBrandName, customUrlBaseValue)
		setSelectedPriceRange(value)
		router.push(url, undefined, {shallow: true, scroll: false}).then(() => {
		})

		reactGA?.event({
			category: gaCategories.plp,
			action: gaEvents.priceRange,
			label: `${gaEvents.priceRange} ${value}`,
			nonInteraction: false
		})
	}

	useEffect(() => {
		setFirstInput(selectedValues[0])
		setSecondInput(selectedValues[1])
	}, [selectedValues])

	const handleFirstInputChange = () => {
		const value = parseInt(firstInputRef?.current?.value || 0, 10)

		if (value >= secondInput) {
			setFirstInput(secondInput - 1)
		} else {
			setFirstInput(value)
		}

		onAfterChange([isTotalPrice ? value : value * planLength, parseInt(secondInput, 10)])
	}

	const handleSecondInputChange = () => {
		const value = parseInt(secondInputRef.current.value || 0, 10)
		setSecondInput(isTotalPrice ? value : value * planLength)
		onAfterChange(([parseInt(firstInput, 10), isTotalPrice ? value : value * planLength]))
	}

	const handleUpdate = values => {
		setFirstInput(values[0])
		setSecondInput(values[1])
		setSelectedPriceRange(values)
	}

	const handleChange = () => {
		reactGA?.event({
			category: gaCategories.plp,
			action: gaEvents.priceRange,
			label: `${gaEvents.priceRange} Total Price checkbox checked`,
			nonInteraction: false
		})
		setIsTotalPrice(prevState => !prevState)
	}

	useEffect(() => {
		if (checkoutData?.planLength === 1) {
			setIsTotalPrice(() => true)
		}
	}, [checkoutData?.planLength])
	return (
		<div className=" pt-2 justify-content-center price-filter-slider">
			<div className="row gx-2 mb-2  pb-1 d-flex align-items-center justify-content-between">
				<div className="col-6">
					<Typography variant="bodySm"
						semanticTag="h4"
						style={{color: "#6C7074"}}>{t("from")}</Typography>
					<input ref={firstInputRef}
						type="number"
						className="w-100"
						value={isTotalPrice ? firstInput : (firstInput / planLength).toFixed()}
						onChange={handleFirstInputChange}/>
				</div>
				<div className="col-6">
					<Typography variant="bodySm"
						semanticTag="h4"
						style={{color: "#6C7074"}}>{t("to")}</Typography>
					<input ref={secondInputRef}
						type="number"
						className="w-100"
						value={isTotalPrice ? secondInput : (secondInput / planLength).toFixed()}
						onChange={handleSecondInputChange}/>
				</div>
			</div>
			<div className="col-12">
				<Slider double
					min={valuesRange.min}
					max={valuesRange.max}
					defaultValue={selectedValues}
					onChange={values => handleUpdate(values)}
					onAfterChange={values => onAfterChange(values)}
					hasPriceTooltip={false}
					paymentValueOne={firstInput ? firstInput : (valuesRange.min / planLength).toFixed()}
					paymentValueTwo={secondInput ? secondInput : (valuesRange.max / planLength).toFixed()}
				/>
			</div>

			<div className="mt-3">
				<Checkbox id="1"
					label={t("total-price")}
					isActive={isTotalPrice || checkoutData?.planLength === 1}
					onChange={handleChange}/>
			</div>
		</div>
	)
}

export default PriceFilterSlider
